import { useMemo } from 'react'
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
  useMaterialReactTable,
} from 'material-react-table'
import { Box, IconButton, Tooltip } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import type { IBrand, INote, IPerfumer, ICountry, IPrice } from '../../../pocketbase/PocketbaseMetadataService'
import {
  ImageCell,
  NameCell,
  BrandCell,
  DescriptionCell,
  CountryCell,
  YearCell,
  PriceCell,
  PerfumerCell,
  NotesCell,
  DateCell,
} from '../components/TableCells'

// Interface for approved submissions with resolved metadata
export interface IApprovedSubmissionWithMetadata {
  id: string
  name: string
  slug: string
  imgUrl: string | null
  description: string
  year: number | null
  brand: IBrand | undefined
  country: ICountry | undefined
  perfumers: IPerfumer[]
  price: IPrice | undefined
  topNotes: INote[]
  middleNotes: INote[]
  bottomNotes: INote[]
  submitted_by: string
  created: string
}

interface ApprovedSubmissionsTableProps {
  data: IApprovedSubmissionWithMetadata[]
  isLoading: boolean
  pagination: MRT_PaginationState
  rowCount: number
  onPaginationChange: (updater: MRT_PaginationState | ((old: MRT_PaginationState) => MRT_PaginationState)) => void
  onView: (submission: IApprovedSubmissionWithMetadata) => void
  onEdit: (submission: IApprovedSubmissionWithMetadata) => void
  onDelete: (submission: IApprovedSubmissionWithMetadata) => void
  searchValue: string
  onSearchChange: (value: string) => void
}

export function ApprovedSubmissionsTable({
  data,
  isLoading,
  pagination,
  rowCount,
  onPaginationChange,
  onView,
  onEdit,
  onDelete,
  searchValue,
  onSearchChange,
}: ApprovedSubmissionsTableProps) {
  const columns = useMemo<MRT_ColumnDef<IApprovedSubmissionWithMetadata>[]>(
    () => [
      {
        accessorKey: 'imgUrl',
        header: 'Image',
        size: 100,
        enableSorting: false,
        Cell: ({ cell }) => <ImageCell imgUrl={cell.getValue<string | null>()} />,
      },
      {
        accessorKey: 'name',
        header: 'Fragrance Name',
        size: 200,
        Cell: ({ cell }) => <NameCell name={cell.getValue<string>()} />,
      },
      {
        accessorKey: 'brand',
        header: 'Brand',
        size: 150,
        Cell: ({ row }) => <BrandCell brand={row.original.brand} />,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 300,
        Cell: ({ cell }) => <DescriptionCell description={cell.getValue<string>()} />,
      },
      {
        accessorKey: 'country',
        header: 'Country',
        size: 150,
        Cell: ({ row }) => <CountryCell country={row.original.country} />,
      },
      {
        accessorKey: 'year',
        header: 'Year',
        size: 100,
        Cell: ({ cell }) => <YearCell year={cell.getValue<number | null>()} />,
      },
      {
        accessorKey: 'price',
        header: 'Price',
        size: 100,
        Cell: ({ row }) => <PriceCell price={row.original.price} />,
      },
      {
        accessorKey: 'perfumers',
        header: 'Perfumer',
        size: 150,
        Cell: ({ row }) => <PerfumerCell perfumers={row.original.perfumers} />,
      },
      {
        accessorKey: 'topNotes',
        header: 'Top Notes',
        size: 200,
        Cell: ({ row }) => <NotesCell notes={row.original.topNotes} />,
      },
      {
        accessorKey: 'middleNotes',
        header: 'Middle Notes',
        size: 200,
        Cell: ({ row }) => <NotesCell notes={row.original.middleNotes} />,
      },
      {
        accessorKey: 'bottomNotes',
        header: 'Base Notes',
        size: 200,
        Cell: ({ row }) => <NotesCell notes={row.original.bottomNotes} />,
      },
      {
        accessorKey: 'created',
        header: 'Submitted',
        size: 150,
        Cell: ({ cell }) => <DateCell date={cell.getValue<string>()} />,
      },
    ],
    [],
  )

  const table = useMaterialReactTable({
    columns,
    data,
    enableEditing: false,
    enableRowActions: true,
    positionActionsColumn: 'first',
    getRowId: (row) => row.id,
    manualPagination: true,
    rowCount,
    state: {
      isLoading,
      pagination,
      globalFilter: searchValue,
      columnPinning: {
        left: ['mrt-row-actions'],
      },
    },
    onPaginationChange,
    onGlobalFilterChange: onSearchChange,
    manualFiltering: true,
    enableColumnFilters: false,
    enableColumnActions: false,
    enableSorting: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableGlobalFilter: true,
    enableColumnPinning: true,
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '0.5rem' }}>
        <Tooltip title="View">
          <IconButton onClick={() => onView(row.original)}>
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton onClick={() => onEdit(row.original)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton onClick={() => onDelete(row.original)} color="error">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    muiTableContainerProps: {
      sx: {
        maxHeight: '100%',
        height: '100%',
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: '1px solid #e0e0e0',
      },
    },
  })

  return <MaterialReactTable table={table} />
}
